import { useCreateOrganizationFromDiscoveryMutation } from "@graphql/auth/auth.g";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { TextField } from "@design-system/TextField/TextField";

import { STORAGE_KEYS } from "@utils/storageKeys";

type Inputs = {
  organizationName: string;
  organizationDomain: string;
  memberName: string;
};

type Props = {
  onSuccess?: () => Promise<void> | void;
};

export const CreateAuthOrganizationForm = ({ onSuccess }: Props) => {
  const intermediateSessionToken = sessionStorage.getItem(
    STORAGE_KEYS.INTERMEDIATE_SESSION_TOKEN,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const createOrganizationFromDiscoveryMutation =
    useCreateOrganizationFromDiscoveryMutation();

  const createOrganizationFromDiscovery =
    createOrganizationFromDiscoveryMutation.mutateAsync;

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!intermediateSessionToken) {
      return;
    }

    await createOrganizationFromDiscovery({
      input: {
        ...data,
        intermediateToken: intermediateSessionToken,
        allowSameEmailSignups: true,
      },
    });
    await onSuccess?.();
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FieldGroup
        label="Organization Name"
        error={errors.organizationName?.message}
        required
      >
        <TextField
          type="text"
          {...register("organizationName", {
            required: { value: true, message: "This field is required" },
          })}
        />
      </FieldGroup>
      <FieldGroup
        label="Organization Domain"
        error={errors.organizationDomain?.message}
        required
      >
        <TextField
          type="text"
          {...register("organizationDomain", {
            required: { value: true, message: "This field is required" },
          })}
        />
      </FieldGroup>
      <FieldGroup label="Full name" error={errors.memberName?.message} required>
        <TextField
          type="text"
          {...register("memberName", {
            required: { value: true, message: "This field is required" },
          })}
        />
      </FieldGroup>
      <Button
        isLoading={createOrganizationFromDiscoveryMutation.isPending}
        type="submit"
        stretch
      >
        {createOrganizationFromDiscoveryMutation.isPending
          ? "Creating organization..."
          : "Create Organization"}
      </Button>
    </Form>
  );
};
